import React, { useState, useRef, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import '../../Css/Botones/botones.css';
import '../../Css/catalogobase/catalogobase.css';
import '../../Css/tables/tabla_consultar.css';

import 'primereact/resources/themes/saga-blue/theme.css';
import 'primereact/resources/primereact.min.css';
import 'primeicons/primeicons.css';

import { Button } from 'primereact/button';
import { InputText } from 'primereact/inputtext';
import { InputTextarea } from 'primereact/inputtextarea';
import { Dropdown } from 'primereact/dropdown';
import { Toast } from 'primereact/toast';
import { Password } from 'primereact/password';

import Navbar from '../../components/navbar/navbar';
import Sidebar from '../../components/sidebar/sidebar';

import { 
    obtenerEntidades,
    eliminarEntidad,
    API_URLS
} from '../../Services/servicioURL';

interface Servidor {
    id: number;
    nombre: string;
    sucursal: string;
    password: string;
    passwordAnydesk?: string;
    usuario?: string;
    ip?: string;
    observaciones?: string;
    fechaCreacion?: Date;
    estatus: string;
}

interface Sucursal {
    id: number;
    nombre: string;
}

const Servidores: React.FC = () => {
    const navigate = useNavigate();
    const [mode, setMode] = useState<'view' | 'new' | 'edit'>('view');
    const [mostrarTabla, setMostrarTabla] = useState(false);
    const [mostrarEstadisticas, setMostrarEstadisticas] = useState(false);
    const [data, setData] = useState<Servidor[]>([]);
    const [sucursales, setSucursales] = useState<Sucursal[]>([]);
    const [searchTerm, setSearchTerm] = useState('');
    const [filtroEstatus, setFiltroEstatus] = useState('');
    const [filtroSucursal, setFiltroSucursal] = useState('');
    const [estadisticas, setEstadisticas] = useState<any>(null);
    const [loading, setLoading] = useState(false);

    // Campos del formulario
    const [id, setId] = useState<number | null>(null);
    const [nombre, setNombre] = useState('');
    const [sucursal, setSucursal] = useState('');
    const [password, setPassword] = useState('');
    const [passwordAnydesk, setPasswordAnydesk] = useState('');
    const [usuario, setUsuario] = useState('');
    const [ip, setIp] = useState('');
    const [observaciones, setObservaciones] = useState('');
    const [estatus, setEstatus] = useState('ACTIVO');

    const toast = useRef<Toast>(null);
    const USUARIOS_AUTORIZADOS = [1, 3, 4, 5, 134];

    // Verificación de acceso
    useEffect(() => {
        const idUsuario = localStorage.getItem('idUsuario');
        if (!idUsuario || !USUARIOS_AUTORIZADOS.includes(parseInt(idUsuario))) {
            toast.current?.show({
                severity: 'error',
                summary: 'Acceso Denegado',
                detail: 'No tienes permisos para acceder a esta sección',
                life: 3000
            });
            navigate('/inicio');
        }
    }, [navigate]);

    const estatusOptions = [
        { label: 'ACTIVO', value: 'ACTIVO' },
        { label: 'INACTIVO', value: 'INACTIVO' }
    ];

    const sucursalesOptions = useMemo(() => {
        return sucursales.map(sucursal => ({
            label: sucursal.nombre,
            value: sucursal.nombre
        }));
    }, [sucursales]);

    const ensureArray = (data: any): any[] => {
        if (Array.isArray(data)) return data;
        if (data?.success && Array.isArray(data.data)) return data.data;
        if (data?.data && Array.isArray(data.data)) return data.data;
        return [];
    };

    const filteredData = useMemo(() => {
        const dataArray = ensureArray(data);
        let filtered = dataArray;

        if (searchTerm) {
            const searchTermLower = searchTerm.toLowerCase();
            filtered = filtered.filter(item =>
                item?.nombre?.toLowerCase().includes(searchTermLower) ||
                item?.sucursal?.toLowerCase().includes(searchTermLower) ||
                item?.usuario?.toLowerCase().includes(searchTermLower) ||
                item?.ip?.toLowerCase().includes(searchTermLower)
            );
        }

        if (filtroEstatus) filtered = filtered.filter(item => item?.estatus === filtroEstatus);
        if (filtroSucursal) filtered = filtered.filter(item => item?.sucursal === filtroSucursal);

        return filtered;
    }, [data, searchTerm, filtroEstatus, filtroSucursal]);

    const registrarServidor = async (servidorData: any) => {
        const response = await fetch(API_URLS.servidores.registrar, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(servidorData),
        });
        if (!response.ok) throw new Error('Error al registrar servidor');
        return await response.json();
    };

    const actualizarServidor = async (id: string, servidorData: any) => {
        const response = await fetch(API_URLS.servidores.actualizar(id), {
            method: 'PUT',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(servidorData),
        });
        if (!response.ok) throw new Error('Error al actualizar servidor');
        return await response.json();
    };

    const cargarSucursales = async () => {
        try {
            const result = await obtenerEntidades(API_URLS.sucursales.obtener);
            const sucursalesArray = ensureArray(result);
            setSucursales(sucursalesArray);
        } catch (error) {
            console.error('Error al cargar sucursales:', error);
            setSucursales([
                { id: 1, nombre: 'Matriz' },
                { id: 2, nombre: 'Sucursal Norte' },
                { id: 3, nombre: 'Sucursal Sur' }
            ]);
        }
    };

    useEffect(() => {
        cargarSucursales();
    }, []);

    useEffect(() => {
        if (mostrarTabla) obtenerServidoresData();
    }, [mostrarTabla]);

    const obtenerServidoresData = async () => {
        try {
            setLoading(true);
            const response = await fetch(API_URLS.servidores.obtener, {
                method: 'GET',
                headers: { 'Content-Type': 'application/json' },
            });

            if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);

            const result = await response.json();
            let servidoresArray = ensureArray(result);

            const servidoresValidados = servidoresArray.map((servidor: any, index: number) => ({
                id: servidor.id || index,
                nombre: servidor.nombre || 'Sin nombre',
                sucursal: servidor.sucursal || 'Sin sucursal',
                password: servidor.password || '',
                passwordAnydesk: servidor.passwordAnydesk || '',
                usuario: servidor.usuario || '',
                ip: servidor.ip || '',
                observaciones: servidor.observaciones || '',
                estatus: servidor.estatus || 'ACTIVO',
                fechaCreacion: servidor.fechaCreacion || new Date()
            }));

            setData(servidoresValidados);
            
            if (servidoresValidados.length === 0) {
                toast.current?.show({
                    severity: 'info',
                    summary: 'Sin datos',
                    detail: 'No se encontraron servidores registrados',
                    life: 3000
                });
            }
            
        } catch (error) {
            console.error('Error al obtener servidores:', error);
            setData([]); 
            toast.current?.show({
                severity: 'error',
                summary: 'Error de conexión',
                detail: 'No se pudieron cargar los servidores',
                life: 5000
            });
        } finally {
            setLoading(false);
        }
    };

    const obtenerEstadisticasData = async () => {
        try {
            const response = await fetch(API_URLS.servidores.obtenerEstadisticas);
            if (!response.ok) throw new Error('Error al obtener estadísticas');
            const result = await response.json();
            
            if (result.success) {
                setEstadisticas(result.data);
                setMostrarEstadisticas(true);
            }
        } catch (error) {
            toast.current?.show({
                severity: 'error',
                summary: 'Error',
                detail: 'Error al cargar las estadísticas',
                life: 3000
            });
        }
    };

    const handleNuevo = () => {
        limpiarFormulario();
        setMode('new');
    };

    const handleEditar = () => {
        if (!id) {
            toast.current?.show({
                severity: 'warn',
                summary: 'Advertencia',
                detail: 'Seleccione un servidor para editar',
                life: 3000
            });
            return;
        }
        setMode('edit');
    };

    const handleGuardar = async () => {
        try {
            if (!nombre || !sucursal || !password) {
                toast.current?.show({
                    severity: 'warn',
                    summary: 'Campos requeridos',
                    detail: 'Debe completar todos los campos obligatorios (nombre, sucursal, password)',
                    life: 3000
                });
                return;
            }

            const servidorData = {
                nombre,
                sucursal,
                password,
                passwordAnydesk,
                usuario,
                ip,
                observaciones,
                estatus
            };

            if (mode === 'new') {
                await registrarServidor(servidorData);
                toast.current?.show({
                    severity: 'success',
                    summary: '¡Éxito!',
                    detail: 'Servidor registrado exitosamente',
                    life: 3000
                });
            } else if (mode === 'edit' && id) {
                await actualizarServidor(id.toString(), servidorData);
                toast.current?.show({
                    severity: 'success',
                    summary: '¡Actualizado!',
                    detail: 'Servidor modificado exitosamente',
                    life: 3000
                });
            }

            limpiarFormulario();
            setMode('view');
            if (mostrarTabla) obtenerServidoresData();
        } catch (error) {
            toast.current?.show({
                severity: 'error',
                summary: 'Error',
                detail: 'No se pudo guardar el servidor',
                life: 3000
            });
        }
    };

    const handleEliminar = async () => {
        if (!id) {
            toast.current?.show({
                severity: 'warn',
                summary: 'Advertencia',
                detail: 'Seleccione un servidor para eliminar',
                life: 3000
            });
            return;
        }

        try {
            const confirmacion = window.confirm(
                `⚠️ ¿Estás seguro de que deseas ELIMINAR PERMANENTEMENTE este servidor?\n\n` +
                `Servidor: ${nombre}\nSucursal: ${sucursal}\n\n⚠️ ESTA ACCIÓN NO SE PUEDE DESHACER ⚠️`
            );
            
            if (!confirmacion) return;

            const textoConfirmacion = prompt('Para confirmar la eliminación permanente, escribe exactamente: ELIMINAR');

            if (textoConfirmacion !== 'ELIMINAR') {
                toast.current?.show({
                    severity: 'warn',
                    summary: 'Eliminación cancelada',
                    detail: 'Texto de confirmación incorrecto',
                    life: 4000
                });
                return;
            }

            await eliminarEntidad(API_URLS.servidores.eliminar(id.toString()));

            toast.current?.show({
                severity: 'success',
                summary: '¡Eliminado!',
                detail: `Servidor "${nombre}" eliminado permanentemente`,
                life: 4000
            });

            limpiarFormulario();
            setMode('view');
            if (mostrarTabla) obtenerServidoresData();
        } catch (error) {
            toast.current?.show({
                severity: 'error',
                summary: 'Error',
                detail: 'No se pudo eliminar el servidor',
                life: 3000
            });
        }
    };

    const handleCancelar = () => {
        limpiarFormulario();
        setMode('view');
    };

    const limpiarFormulario = () => {
        setId(null);
        setNombre('');
        setSucursal('');
        setPassword('');
        setPasswordAnydesk('');
        setUsuario('');
        setIp('');
        setObservaciones('');
        setEstatus('ACTIVO');
    };

    const handleSeleccionarServidor = (servidor: Servidor) => {
        setId(servidor.id);
        setNombre(servidor.nombre);
        setSucursal(servidor.sucursal);
        setPassword(servidor.password);
        setPasswordAnydesk(servidor.passwordAnydesk || '');
        setUsuario(servidor.usuario || '');
        setIp(servidor.ip || '');
        setObservaciones(servidor.observaciones || '');
        setEstatus(servidor.estatus);
        setMostrarTabla(false);
        setMode('view');
        setSearchTerm('');
        setFiltroEstatus('');
        setFiltroSucursal('');
    };

    const getModeIndicator = () => {
        switch (mode) {
            case 'new': return { text: 'NUEVO SERVIDOR', color: '#10b981', icon: 'pi-plus' };
            case 'edit': return { text: 'EDITANDO SERVIDOR', color: '#f59e0b', icon: 'pi-pencil' };
            default: return { text: 'MODO CONSULTA', color: '#6b7280', icon: 'pi-eye' };
        }
    };

    const getEstatusColor = (estatus: string) => {
        return estatus === 'ACTIVO' ? '#10b981' : '#6b7280';
    };

    const modeInfo = getModeIndicator();

    const containerStyle = {
        backgroundImage: 'url(/dist/img/FondoLogin.png)',
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundRepeat: 'no-repeat',
        backgroundAttachment: 'fixed',
        minHeight: '100vh',
        padding: '20px'
    };

    const formStyle = {
        backgroundColor: 'rgba(255, 255, 255, 0.95)',
        borderRadius: '16px',
        padding: '24px',
        maxWidth: '1000px',
        margin: '0 auto',
        boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1)',
        border: '1px solid rgba(255, 255, 255, 0.2)'
    };

    const modalStyle = {
        position: 'fixed' as const,
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.6)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 1000
    };

    const modalContentStyle = {
        backgroundColor: 'white',
        borderRadius: '12px',
        padding: '24px',
        maxWidth: '1200px',
        width: '95%',
        maxHeight: '85vh',
        display: 'flex',
        flexDirection: 'column' as const
    };

    return (
        <>
            <Navbar />
            <Sidebar />
            <Toast ref={toast} />

            <div className="content-wrapper" style={containerStyle}>
                {/* Modal de estadísticas */}
                {mostrarEstadisticas && estadisticas && (
                    <div style={modalStyle} onClick={() => setMostrarEstadisticas(false)}>
                        <div style={modalContentStyle} onClick={(e) => e.stopPropagation()}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
                                <h3 style={{ margin: 0, color: '#1f2937', fontSize: '1.5rem', fontWeight: '600' }}>
                                    📊 Estadísticas de Servidores
                                </h3>
                                <Button
                                    icon="pi pi-times"
                                    className="p-button-text p-button-rounded"
                                    onClick={() => setMostrarEstadisticas(false)}
                                />
                            </div>

                            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))', gap: '16px', marginBottom: '24px' }}>
                                
                                {estadisticas.por_estatus?.map((item: any, index: number) => (
                                    <div key={index} style={{ 
                                        textAlign: 'center', 
                                        padding: '16px', 
                                        backgroundColor: item.estatus === 'ACTIVO' ? '#f0fdf4' : '#fef2f2', 
                                        borderRadius: '8px' 
                                    }}>
                                        <div style={{ fontSize: '2rem', fontWeight: 'bold', color: item.estatus === 'ACTIVO' ? '#16a34a' : '#dc2626' }}>
                                            {item.cantidad}
                                        </div>
                                        <div style={{ fontSize: '0.9rem', color: '#64748b' }}>{item.estatus}</div>
                                    </div>
                                ))}
                            </div>

                            <h4 style={{ marginBottom: '16px', color: '#1f2937' }}>Servidores por Sucursal</h4>
                            <div style={{ maxHeight: '200px', overflow: 'auto' }}>
                                {estadisticas.por_sucursal?.map((item: any, index: number) => (
                                    <div key={index} style={{
                                        display: 'flex',
                                        justifyContent: 'space-between',
                                        padding: '8px 12px',
                                        backgroundColor: index % 2 === 0 ? '#f9fafb' : 'white',
                                        borderRadius: '4px',
                                        marginBottom: '4px'
                                    }}>
                                        <span>{item.sucursal}</span>
                                        <span style={{ fontWeight: 'bold', color: '#3b82f6' }}>{item.cantidad}</span>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>
                )}

                {/* Modal de la tabla */}
                {mostrarTabla && (
                    <div style={modalStyle} onClick={() => setMostrarTabla(false)}>
                        <div style={modalContentStyle} onClick={(e) => e.stopPropagation()}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
                                <h3 style={{ margin: 0, color: '#1f2937', fontSize: '1.5rem', fontWeight: '600' }}>
                                    🖥️ Consultar Servidores
                                </h3>
                                <Button
                                    icon="pi pi-times"
                                    className="p-button-text p-button-rounded"
                                    onClick={() => setMostrarTabla(false)}
                                />
                            </div>

                            {/* Filtros */}
                            <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr', gap: '16px', marginBottom: '20px' }}>
                                <InputText
                                    style={{ width: '100%', height: '40px', paddingLeft: '40px' }}
                                    placeholder="Buscar por nombre, sucursal, usuario, IP..."
                                    value={searchTerm}
                                    onChange={(e) => setSearchTerm(e.target.value)}
                                />
                                <Dropdown
                                    value={filtroEstatus}
                                    options={[{ label: 'Todos los estatus', value: '' }, ...estatusOptions]}
                                    onChange={(e) => setFiltroEstatus(e.value)}
                                    placeholder="Filtrar por estatus"
                                    style={{ height: '40px' }}
                                />
                                <Dropdown
                                    value={filtroSucursal}
                                    options={[{ label: 'Todas las sucursales', value: '' }, ...sucursalesOptions]}
                                    onChange={(e) => setFiltroSucursal(e.value)}
                                    placeholder="Filtrar por sucursal"
                                    style={{ height: '40px' }}
                                />
                            </div>

                            {/* Tabla */}
                            <div style={{ border: '1px solid #e5e7eb', borderRadius: '8px', overflow: 'hidden', flex: 1 }}>
                                <div style={{ backgroundColor: '#f8fafc', borderBottom: '2px solid #e5e7eb' }}>
                                    <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                                        <thead>
                                            <tr>
                                                <th style={{ padding: '12px 8px', textAlign: 'left', fontWeight: '600', color: '#374151' }}>Nombre</th>
                                                <th style={{ padding: '12px 8px', textAlign: 'left', fontWeight: '600', color: '#374151' }}>Sucursal</th>
                                            </tr>
                                        </thead>
                                    </table>                                                                                    
                                </div>
                                
                                <div style={{ flex: 1, overflowY: 'auto', maxHeight: 'calc(85vh - 250px)' }}>
                                    <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                                        <tbody>
                                            {loading ? (
                                                <tr>
                                                    <td colSpan={7} style={{ padding: '40px', textAlign: 'center', color: '#6b7280' }}>
                                                        🔄 Cargando servidores...
                                                    </td>
                                                </tr>
                                            ) : filteredData.length > 0 ? (
                                                filteredData.map((item, index) => (
                                                    <tr 
                                                        key={item.id} 
                                                        onClick={() => handleSeleccionarServidor(item)}
                                                        style={{
                                                            cursor: 'pointer',
                                                            backgroundColor: index % 2 === 0 ? '#ffffff' : '#f9fafb'
                                                        }}
                                                        onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#e0f2fe'}
                                                        onMouseLeave={(e) => e.currentTarget.style.backgroundColor = index % 2 === 0 ? '#ffffff' : '#f9fafb'}
                                                    >
                                                        <td style={{ padding: '12px 8px', borderBottom: '1px solid #f3f4f6' }}>
                                                            <div style={{ fontSize: '0.9rem', fontWeight: '500' }}>{item.nombre || '-'}</div>
                                                        </td>
                                                        <td style={{ padding: '12px 8px', borderBottom: '1px solid #f3f4f6', fontSize: '0.9rem' }}>
                                                            {item.sucursal || '-'}
                                                        </td>
                                                        
                                                        
                                                         </tr>
                                                ))
                                            ) : (
                                                <tr>
                                                    <td colSpan={7} style={{ padding: '40px', textAlign: 'center', color: '#6b7280' }}>
                                                        {Array.isArray(data) && data.length === 0 ? 
                                                            '📝 No hay servidores registrados' : 
                                                            '🔍 No se encontraron servidores que coincidan con los filtros'
                                                        }
                                                    </td>
                                                </tr>
                                            )}
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                        </div>
                    </div>
                )}

                {/* Formulario principal */}
                <div style={formStyle}>
                    {/* Header */}
                    <div style={{ marginBottom: '24px' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
                            <h2 style={{ margin: 0, color: '#1f2937', fontSize: '1.8rem', fontWeight: '700' }}>
                                🖥️ Gestión de Servidores
                            </h2>
                            <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                                <div style={{
                                    padding: '4px 8px',
                                    backgroundColor: '#fef3c7',
                                    color: '#92400e',
                                    borderRadius: '6px',
                                    fontSize: '0.75rem',
                                    fontWeight: '600'
                                }}>
                                    🔒 ACCESO RESTRINGIDO
                                </div>
                                <div style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '8px',
                                    padding: '8px 16px',
                                    backgroundColor: modeInfo.color,
                                    color: 'white',
                                    borderRadius: '20px',
                                    fontSize: '0.85rem',
                                    fontWeight: '600'
                                }}>
                                    <i className={`pi ${modeInfo.icon}`}></i>
                                    {modeInfo.text}
                                </div>
                            </div>
                        </div>
                        <div style={{ height: '2px', background: 'linear-gradient(90deg, #3b82f6, #10b981)', borderRadius: '1px' }}></div>
                    </div>

                    {/* Formulario */}
                    <div style={{ marginBottom: '24px' }}>
                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '20px' }}>
                            {/* Nombre */}
                            <div>
                                <label style={{ display: 'block', fontSize: '0.95rem', fontWeight: '600', marginBottom: '8px', color: '#374151' }}>
                                    🖥️ Nombre del Servidor <span style={{ color: '#ef4444' }}>*</span>
                                </label>
                                <InputText
                                    value={nombre}
                                    onChange={(e) => setNombre(e.target.value)}
                                    disabled={mode === 'view'}
                                    placeholder="Ej: SERVER-01, WEB-SERVER, etc."
                                    style={{ width: '100%', height: '40px' }}
                                />
                            </div>

                            {/* Sucursal */}
                            <div>
                                <label style={{ display: 'block', fontSize: '0.95rem', fontWeight: '600', marginBottom: '8px', color: '#374151' }}>
                                    🏢 Sucursal <span style={{ color: '#ef4444' }}>*</span>
                                </label>
                                <Dropdown
                                    value={sucursal}
                                    options={sucursalesOptions}
                                    onChange={(e) => setSucursal(e.value)}
                                    placeholder="Seleccione la sucursal"
                                    disabled={mode === 'view'}
                                    style={{ width: '100%', height: '40px' }}
                                    filter
                                />
                            </div>
                             

                             {/* Usuario */}
                            <div>
                                <label style={{ display: 'block', fontSize: '0.95rem', fontWeight: '600', marginBottom: '8px', color: '#374151' }}>
                                    👤 Usuario de Acceso
                                </label>
                                <InputText
                                    value={usuario}
                                    onChange={(e) => setUsuario(e.target.value)}
                                    disabled={mode === 'view'}
                                    placeholder="Ej: root, etc."
                                    style={{ width: '100%', height: '40px' }}
                                />
                            </div>
                            
                            {/* Password */}
                            <div>
                                <label style={{ display: 'block', fontSize: '0.95rem', fontWeight: '600', marginBottom: '8px', color: '#374151' }}>
                                    🔑 Contraseña <span style={{ color: '#ef4444' }}>*</span>
                                </label>
                                <Password
                                    value={password}
                                    onChange={(e) => setPassword(e.target.value)}
                                    disabled={mode === 'view'}
                                    placeholder="Ingrese la contraseña"
                                    toggleMask
                                    style={{ width: '100%', height: '40px' }}
                                    inputStyle={{ width: '100%', height: '40px' }}
                                    feedback={mode !== 'view'}
                                />
                            </div>

                            {/* Password AnyDesk */}
                            <div>
                                <label style={{ display: 'block', fontSize: '0.95rem', fontWeight: '600', marginBottom: '8px', color: '#374151' }}>
                                    🖥️ Contraseña AnyDesk
                                </label>
                                <Password
                                    value={passwordAnydesk}
                                    onChange={(e) => setPasswordAnydesk(e.target.value)}
                                    disabled={mode === 'view'}
                                    placeholder="AnyDesk"
                                    toggleMask
                                    style={{ width: '100%', height: '40px' }}
                                    inputStyle={{ width: '100%', height: '40px' }}
                                    feedback={false}
                                />
                            </div>

                            

                            {/* IP */}
                            <div>
                                <label style={{ display: 'block', fontSize: '0.95rem', fontWeight: '600', marginBottom: '8px', color: '#374151' }}>
                                    🌐 Dirección IP
                                </label>
                                <InputText
                                    value={ip}
                                    onChange={(e) => setIp(e.target.value)}
                                    disabled={mode === 'view'}
                                    placeholder="Ej: 192.168.1.100"
                                    style={{ width: '100%', height: '40px' }}
                                />
                            </div>

                            {/* Estatus */}
                            <div>
                                <label style={{ display: 'block', fontSize: '0.95rem', fontWeight: '600', marginBottom: '8px', color: '#374151' }}>
                                    📊 Estatus
                                </label>
                                <Dropdown
                                    value={estatus}
                                    options={estatusOptions}
                                    onChange={(e) => setEstatus(e.value)}
                                    disabled={mode === 'view'}
                                    style={{ width: '100%', height: '40px' }}
                                />
                            </div>

                            {/* Observaciones */}
                            <div style={{ gridColumn: '1 / -1' }}>
                                <label style={{ display: 'block', fontSize: '0.95rem', fontWeight: '600', marginBottom: '8px', color: '#374151' }}>
                                    📋 Observaciones
                                </label>
                                <InputTextarea
                                    value={observaciones}
                                    onChange={(e) => setObservaciones(e.target.value)}
                                    disabled={mode === 'view'}
                                    placeholder="Notas adicionales sobre el servidor"
                                    rows={3}
                                    style={{ width: '100%', resize: 'vertical' }}
                                />
                            </div>
                        </div>
                    </div>

                    {/* Botones */}
                    <div style={{ display: 'flex', gap: '12px', justifyContent: 'center', flexWrap: 'wrap' }}>
                        {mode === 'view' ? (
                            <>
                                <Button
                                    icon="pi pi-search"
                                    label="Consultar"
                                    className="p-button-info p-button-raised"
                                    onClick={() => setMostrarTabla(true)}
                                    style={{ height: '44px', fontSize: '0.95rem', fontWeight: '600', borderRadius: '8px', minWidth: '140px' }}
                                />
                                <Button
                                    icon="pi pi-chart-bar"
                                    label="Estadísticas"
                                    className="p-button-help p-button-raised"
                                    onClick={obtenerEstadisticasData}
                                    style={{ height: '44px', fontSize: '0.95rem', fontWeight: '600', borderRadius: '8px', minWidth: '120px' }}
                                />
                                <Button
                                    icon="pi pi-plus"
                                    label="Nuevo"
                                    className="p-button-success p-button-raised"
                                    onClick={handleNuevo}
                                    style={{ height: '44px', fontSize: '0.95rem', fontWeight: '600', borderRadius: '8px', minWidth: '120px' }}
                                />
                                <Button
                                    icon="pi pi-pencil"
                                    label="Editar"
                                    className="p-button-warning p-button-raised"
                                    onClick={handleEditar}
                                    disabled={!id}
                                    style={{ height: '44px', fontSize: '0.95rem', fontWeight: '600', borderRadius: '8px', minWidth: '120px', opacity: !id ? 0.5 : 1 }}
                                />
                                <Button
                                    icon="pi pi-trash"
                                    label="Eliminar"
                                    className="p-button-danger p-button-raised"
                                    onClick={handleEliminar}
                                    disabled={!id}
                                    style={{ height: '44px', fontSize: '0.95rem', fontWeight: '600', borderRadius: '8px', minWidth: '120px', opacity: !id ? 0.5 : 1 }}
                                />
                            </>
                        ) : (
                            <>
                                <Button
                                    icon="pi pi-check"
                                    label="Guardar"
                                    className="p-button-success p-button-raised"
                                    onClick={handleGuardar}
                                    style={{ height: '44px', fontSize: '0.95rem', fontWeight: '600', borderRadius: '8px', minWidth: '140px' }}
                                />
                                <Button
                                    icon="pi pi-times"
                                    label="Cancelar"
                                    className="p-button-secondary p-button-raised"
                                    onClick={handleCancelar}
                                    style={{ height: '44px', fontSize: '0.95rem', fontWeight: '600', borderRadius: '8px', minWidth: '120px' }}
                                />
                            </>
                        )}
                    </div>

                    {/* Nota informativa */}
                    {mode === 'view' && id && (
                        <div style={{
                            marginTop: '16px',
                            padding: '12px',
                            backgroundColor: '#f0f9ff',
                            border: '1px solid #0ea5e9',
                            borderRadius: '8px',
                            fontSize: '0.85rem',
                            color: '#0369a1',
                            textAlign: 'center'
                        }}>
                            💡 <strong>Tip:</strong> Para desactivar un servidor sin eliminarlo, usa "Editar" y cambia el estatus a "INACTIVO"
                        </div>
                    )}
                </div>
            </div>
        </>
    );
};

export default Servidores;